/**
 * Texture Import Settings & Compiled Cache Types
 * 
 * Unity-like texture import pipeline:
 * - Original files stored as source of truth
 * - Import settings define output format, compression, etc.
 * - Compiled cache stores processed textures
 * 
 * @module @engine/aid
 */

// ============================================
// TEXTURE IMPORT SETTINGS
// ============================================

/**
 * Output format for compiled texture
 */
export type TextureFormat = 'png' | 'webp' | 'source';

/**
 * Compression quality level
 */
export type CompressionQuality = 'none' | 'low' | 'medium' | 'high';

/**
 * Maximum texture dimension (power of 2)
 */
export type MaxTextureSize = 32 | 64 | 128 | 256 | 512 | 1024 | 2048 | 4096 | 8192;

/**
 * Alpha channel handling
 */
export type AlphaMode = 'none' | 'straight' | 'premultiplied';

/**
 * Texture filtering mode
 */
export type FilterMode = 'point' | 'bilinear' | 'trilinear';

/**
 * Texture wrap mode
 */
export type WrapMode = 'clamp' | 'repeat' | 'mirror';

/**
 * Channel format / pixel layout
 */
export type ChannelFormat = 'rgba' | 'argb' | 'bgra' | 'rgb' | 'bgr' | 'rg' | 'r';

/**
 * Swizzle channel source
 * r,g,b,a = source channel
 * R,G,B,A = inverted channel (1 - value)
 * 0 = zero (0)
 * 1 = one (255)
 */
export type SwizzleChannel = 'r' | 'g' | 'b' | 'a' | 'R' | 'G' | 'B' | 'A' | '0' | '1';

/**
 * Swizzle mask - 4 characters defining output channel sources
 * Examples:
 * - "rgba" = no change (default)
 * - "bgra" = swap red and blue
 * - "rrrr" = grayscale from red
 * - "rgb1" = opaque (alpha = 1)
 * - "aaaa" = alpha to all channels
 * - "RGBA" = inverted image
 */
export type SwizzleMask = `${SwizzleChannel}${SwizzleChannel}${SwizzleChannel}${SwizzleChannel}`;

/**
 * Sprite import mode
 */
export type SpriteMode = 'single' | 'multiple' | 'polygon';

/**
 * Texture importer parameters (stored in AssetEntry.importerParams)
 */
export interface TextureImportSettings {
  /** Output format for compiled texture */
  format: TextureFormat;
  
  /** Compression quality (for lossy formats) */
  compression: CompressionQuality;
  
  /** WebP quality (0-100, only for webp format) */
  webpQuality: number;
  
  /** Maximum dimension (downscale if larger) */
  maxSize: MaxTextureSize;
  
  /** Alpha channel handling */
  alphaMode: AlphaMode;
  
  /** Generate mipmaps */
  generateMipmaps: boolean;
  
  /** Texture filtering */
  filterMode: FilterMode;
  
  /** Texture wrapping */
  wrapMode: WrapMode;
  
  /** sRGB color space (vs linear) */
  sRGB: boolean;
  
  /** Channel format / pixel layout */
  channelFormat: ChannelFormat;
  
  /** Custom swizzle mask (overrides channelFormat if not "rgba") */
  swizzle: SwizzleMask;
  
  /** Sprite mode for atlas slicing */
  spriteMode: SpriteMode;
  
  /** Pixels per unit (for sprites) */
  pixelsPerUnit: number;
  
  /** Pivot point (0-1 range) */
  pivot: { x: number; y: number };
  
  /** Border padding for sprite (for 9-slice) */
  border: { left: number; right: number; top: number; bottom: number };
  
  /** Readable/writable at runtime */
  readable: boolean;
}

/**
 * Default texture import settings
 */
export const DEFAULT_TEXTURE_SETTINGS: TextureImportSettings = {
  format: 'source',      // Keep original format by default
  compression: 'none',
  webpQuality: 80,
  maxSize: 2048,
  alphaMode: 'straight',
  generateMipmaps: false, // 2D games usually don't need mipmaps
  filterMode: 'point',    // Pixel art friendly default
  wrapMode: 'clamp',
  sRGB: true,
  channelFormat: 'rgba',  // Standard WebGL format
  swizzle: 'rgba',        // No swizzle by default
  spriteMode: 'single',
  pixelsPerUnit: 1,
  pivot: { x: 0.5, y: 0.5 },
  border: { left: 0, right: 0, top: 0, bottom: 0 },
  readable: false,
};


// ============================================
// COMPILED ARTIFACT
// ============================================

/**
 * Metadata for a compiled texture artifact
 */
export interface CompiledTextureInfo {
  /** Source asset GUID */
  sourceGuid: string;
  
  /** Source asset path */
  sourcePath: string;
  
  /** Compiled artifact path in cache */
  compiledPath: string;
  
  /** Hash of source content + settings (for cache invalidation) */
  compiledHash: string;
  
  /** Output format used */
  format: TextureFormat;
  
  /** Actual dimensions after processing (base level) */
  width: number;
  height: number;
  
  /** Whether alpha channel is present */
  hasAlpha: boolean;
  
  /** File size of compiled artifact (all mipmaps) */
  size: number;
  
  /** Number of mipmap levels (1 = base only, 0 = full chain) */
  mipmapCount: number;
  
  // GPU settings for renderer
  /** Texture filtering mode */
  filterMode: FilterMode;
  
  /** Texture wrap mode */
  wrapMode: WrapMode;
  
  /** Channel format / pixel layout */
  channelFormat: ChannelFormat;
  
  /** Custom swizzle mask applied (if any) */
  swizzle: SwizzleMask;
  
  /** sRGB color space flag */
  sRGB: boolean;
  
  /** Compilation timestamp */
  compiledAt: Date;
  
  /** Full settings used for compilation */
  settings: TextureImportSettings;
}

// ============================================
// AUDIO IMPORT SETTINGS (for future)
// ============================================

/**
 * Audio output format
 */
export type AudioFormat = 'ogg' | 'mp3' | 'wav' | 'source';

/**
 * Audio import settings
 */
export interface AudioImportSettings {
  /** Output format */
  format: AudioFormat;
  
  /** Force mono (for SFX, halves memory) */
  forceMono: boolean;
  
  /** Sample rate (0 = keep original) */
  sampleRate: 0 | 22050 | 44100 | 48000;
  
  /** Compression quality for lossy formats (0-1) */
  quality: number;
  
  /** Load type */
  loadType: 'decompressOnLoad' | 'streaming' | 'compressed';
  
  /** Preload on scene start */
  preload: boolean;
  
  /** Normalize volume */
  normalize: boolean;
}

/**
 * Default audio import settings
 */
export const DEFAULT_AUDIO_SETTINGS: AudioImportSettings = {
  format: 'ogg',
  forceMono: false,
  sampleRate: 0,
  quality: 0.7,
  loadType: 'decompressOnLoad',
  preload: false,
  normalize: false,
};

// ============================================
// SCRIPT IMPORT SETTINGS
// ============================================

// NOTE: Project-level "compilation settings" (optimize/debug/sourceMap/exportWat/memory/SIMD) were
// AssemblyScript/WASM-era config. TypeScript behaviours are stripped by esbuild/swc with no such knobs,
// and nothing consumed these settings — the whole CompilationSettings type + UI tab were removed.

/**
 * Per-script import settings (script-specific overrides)
 */
export interface ScriptImportSettings {
  /** Script execution order (lower = runs first) */
  executeOrder: number;
}

/**
 * Default script import settings
 */
export const DEFAULT_SCRIPT_SETTINGS: ScriptImportSettings = {
  executeOrder: 0,
};

/**
 * Compiled script cache metadata
 */
// (Removed CompiledScriptInfo — a WASM-era type: wasmPath/watPath/wasmSize/compiledHash + the deleted
// CompilationSettings. Nothing consumed it. TS behaviours produce plain JS, no such artifact metadata.)
